import { useRef } from 'react';

type Task<T> = () => Promise<T>;

export default function useAsyncQueue() {
  const ref = useRef(
    new Map<
      string,
      {
        active: boolean;
        queue: (() => Promise<void>)[];
      }
    >(),
  );

  const enqueue = <T>(id: string, task: Task<T>): Promise<T> =>
    new Promise<T>((resolve, reject) => {
      if (!ref.current.has(id)) {
        ref.current.set(id, { active: false, queue: [] });
      }

      const context = ref.current.get(id);
      context?.queue.push(async () => {
        try {
          const result = await task();
          resolve(result);
        } catch (err) {
          reject(err);
        }
      });
      processQueue(id);
    });

  const processQueue = async (id: string) => {
    const context = ref.current.get(id);
    if (!context || context.active || !context.queue.length) return;

    context.active = true;

    const task = context.queue.shift();
    if (task) {
      await task();
      context.active = false;
      processQueue(id);
    }
  };

  return enqueue;
}
